import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";

const MAX_ATTEMPTS = 10;
const INTERVAL = 2000;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useLang();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout

  const poll = useCallback(async (attempt) => {
    if (attempt >= MAX_ATTEMPTS) { setState("timeout"); return; }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") { setState("success"); return; }
      if (data.status === "expired") { setState("failed"); return; }
      setTimeout(() => poll(attempt + 1), INTERVAL);
    } catch {
      setTimeout(() => poll(attempt + 1), INTERVAL);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    poll(0);
  }, [sessionId, poll]);

  const icon = {
    checking: <Loader2 size={44} className="animate-spin text-white" />,
    success: <CheckCircle2 size={44} className="text-emerald-400" />,
    failed: <XCircle size={44} className="text-red-400" />,
    timeout: <XCircle size={44} className="text-amber-400" />,
  }[state];

  const title = { checking: t("payment.checking"), success: t("payment.success"), failed: t("payment.failed"), timeout: t("payment.failed") }[state];
  const desc = { checking: "", success: t("payment.successDesc"), failed: "", timeout: t("payment.timeout") }[state];

  return (
    <div className="grain min-h-screen bg-[#050505] flex flex-col">
      <div className="p-5 md:p-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft size={16} /> {t("payment.backHome")}</Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-md" data-testid="payment-return">
          <img src={LOGO_URL} alt="AURA+" className="h-10 w-auto mx-auto mix-blend-screen opacity-95 mb-10" />
          <div className="flex justify-center mb-6">{icon}</div>
          <h1 data-testid="payment-status-title" className="font-display text-3xl md:text-4xl font-bold text-white">{title}</h1>
          {desc && <p className="mt-4 text-zinc-400">{desc}</p>}
          {state !== "checking" && (
            <div className="mt-10 flex items-center justify-center gap-3">
              <Link to="/" className="rounded-full bg-white text-black font-semibold px-6 py-3 hover:bg-zinc-200 transition-colors">{t("payment.backHome")}</Link>
              {state !== "success" && <Link to="/#prodotto" className="rounded-full border border-white/20 text-white px-6 py-3 hover:border-white/50 transition-colors">{t("payment.backProduct")}</Link>}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
